/**
 * @page CheckInPage
 * @description 快速報到搜尋頁（UI-3-4）
 * 模糊搜尋 + 欠款/黑名單警示
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import type { MemberDto } from '@pokerroom/types';

type SearchResult = {
  member: MemberDto;
  hasDebt: boolean;
  isBlacklisted: boolean;
};

export default function CheckInPage() {
  const { t } = useTranslation('member');
  const { t: tc } = useTranslation('common');

  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const timerRef = useRef<number>();

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Debounce 300ms
  const handleSearch = (v: string) => {
    setQuery(v);
    clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setDebouncedQ(v), 300);
  };

  const { data: results = [], isFetching } = useQuery<SearchResult[]>({
    queryKey: ['check-in-search', debouncedQ],
    queryFn: () =>
      api.get<SearchResult[]>(`/members/check-in/search?q=${encodeURIComponent(debouncedQ)}`),
    enabled: debouncedQ.length >= 1,
  });

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-base-content mb-6">{t('checkIn')}</h1>

      {/* 搜尋框 */}
      <div className="relative mb-6">
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t('searchPlaceholder')}
          className="w-full px-4 py-3 pl-10 rounded-xl border border-base-300 bg-base-200
                     text-base-content text-base focus:outline-hidden focus:ring-2 focus:ring-primary shadow-xs"
        />
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        {isFetching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* 搜尋結果 */}
      {debouncedQ && (
        <div className="space-y-3">
          {results.length === 0 && !isFetching ? (
            <div className="text-center py-10 text-base-content/40">
              <p>{t('noMemberFound', { query: debouncedQ })}</p>
              <Link
                to="/members/create"
                className="text-primary text-sm mt-2 inline-block hover:underline"
              >
                {t('quickCreateMember')}
              </Link>
            </div>
          ) : (
            results.map(({ member, hasDebt, isBlacklisted }) => (
              <div
                key={member.id}
                className={`p-4 rounded-xl border bg-base-200 shadow-xs
                  ${isBlacklisted ? 'border-red-400 bg-red-50' : hasDebt ? 'border-orange-300 bg-orange-50' : 'border-base-300'}`}
              >
                {/* 警告橫幅 */}
                {isBlacklisted && (
                  <div className="text-xs text-red-700 font-medium mb-2 flex items-center gap-1">
                    {t('blacklistForbidden')}
                  </div>
                )}
                {!isBlacklisted && hasDebt && (
                  <div className="text-xs text-orange-700 font-medium mb-2 flex items-center gap-1">
                    {t('debtAmount', {
                      amount: (Number.isFinite(parseFloat(member.totalDebt))
                        ? parseFloat(member.totalDebt)
                        : 0
                      ).toLocaleString(),
                    })}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-base-content">{member.nickname}</p>
                    <p className="text-xs text-base-content/60 mt-0.5">
                      {member.memberNo} {member.phone ? `· ${member.phone}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-base-content/60">{t('ledgerTabPlayer')}</p>
                      <p className="font-medium text-primary">
                        {(member.playerPoints ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <Link
                      to={`/members/${member.id}`}
                      className="px-4 py-2 rounded-lg bg-primary text-primary-content text-sm font-medium hover:bg-primary/90"
                    >
                      {t('confirmCheckIn')}
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {!debouncedQ && (
        <div className="text-center py-16 text-base-content/30">
          <div className="text-5xl mb-4">🔍</div>
          <p>{t('searchPrompt')}</p>
        </div>
      )}
    </div>
  );
}
